import { Box, CircularProgress } from "@mui/material";
import SkuCoverImageField from "components/SkuCoverImageField";
import SortableTable from "components/SortableTable";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useIntl } from "react-intl";
import {
  AttributeFilter,
  ListRequestOfAttributeFilter,
  ListResponseOfSkuAttributeByType,
} from "services/KnowfullyAdminClient";
import {
  AttributeCodeConstants,
  AttributeDisplayTypes,
} from "types/common-types";
import { AttributeWithId } from "types/sku-related-types";
import {
  buildAttributeDataCells,
  flattenLocationMetaValues,
  flattenSponsorMetaValues,
  getAttributeCodeConstant,
  hasLocationAttributeGroup,
  useLocationLookups,
} from "../SkuAttributes/attributeColumnsUtils";

const SkuAttributesList = ({
  skuId,
  organizationAttributeTypeId,
}: {
  skuId: string;
  organizationAttributeTypeId?: string;
}) => {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();

  const request = ListRequestOfAttributeFilter.fromJS({
    sortField: "createdOn",
    sortDescending: true,
    parameters: AttributeFilter.fromJS({
      organizationAttributeTypeId: organizationAttributeTypeId,
    }),
  });

  const { data, isFetching } = useStrictQuery<ListResponseOfSkuAttributeByType>(
    ["skuAttributesByType", skuId, request],
    () => skuService.getSkuAttributesByType(skuId, request),
    { enabled: !!skuId }
  );

  const visibleGroups = data?.data?.filter((x) =>
    organizationAttributeTypeId
      ? x.organizationAttributeTypeId === organizationAttributeTypeId
      : x.attributeDisplayTypeId === AttributeDisplayTypes.AttributeSection
  );
  const locationLookups = useLocationLookups(
    hasLocationAttributeGroup(visibleGroups)
  );

  const renderAttributes = () =>
    visibleGroups
      ?.sort((a, b) => (a?.type! > b?.type! ? 1 : -1))
      .map((attributeGroup, index) => {
        const codeConstant = getAttributeCodeConstant(
          attributeGroup.attributes
        );

        if (codeConstant === AttributeCodeConstants.CoverImage) {
          const [coverImage] = (attributeGroup.attributes ?? []) as AttributeWithId[];
          return (
            <Box key={index} my={2}>
              <SkuCoverImageField value={coverImage?.value} readMode />
            </Box>
          );
        }

        const attributes =
          codeConstant === AttributeCodeConstants.Locations
            ? flattenLocationMetaValues(
                attributeGroup.attributes as AttributeWithId[],
                locationLookups
              )
            : codeConstant === AttributeCodeConstants.Sponsors
            ? flattenSponsorMetaValues(
                attributeGroup.attributes as AttributeWithId[],
                t
              )
            : (attributeGroup.attributes as AttributeWithId[]);

        const cells = buildAttributeDataCells({
          codeConstant,
          allowDates: !!attributeGroup.allowDates,
          defaultAccessor: "displayText",
          t,
        });

        return (
          <div key={index}>
            <SortableTable
              {...{
                rows: attributes,
                loading: isFetching,
                dense: true,
                cells,
                tableRowPerPage: 5,
                defaultOrderBy: "createdOn",
                title: attributeGroup.type,
              }}
            />
          </div>
        );
      });

  return (
    <>
      {isFetching ? (
        <CircularProgress style={{ float: "left" }} size={24} />
      ) : (
        renderAttributes()
      )}
    </>
  );
};

export default withErrorBoundary(SkuAttributesList);
